import { useState, useMemo } from 'react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';

export function PercentageCalculator() {
  return (
    <div>
      <ToolHeader
        toolId="percentage-calculator"
        title="Percentage calculator"
        description="What is X% of Y? X is what % of Y? Increase/decrease, change, and reverse calculations."
      />
      <Tabs defaultValue="of">
        <TabsList className="mb-4 flex-wrap !h-auto">
          <TabsTrigger value="of">X% of Y</TabsTrigger>
          <TabsTrigger value="is">X is what % of Y</TabsTrigger>
          <TabsTrigger value="change">% change</TabsTrigger>
          <TabsTrigger value="add">Add %</TabsTrigger>
          <TabsTrigger value="sub">Subtract %</TabsTrigger>
        </TabsList>
        <TabsContent value="of"><Of /></TabsContent>
        <TabsContent value="is"><Is /></TabsContent>
        <TabsContent value="change"><Change /></TabsContent>
        <TabsContent value="add"><Add /></TabsContent>
        <TabsContent value="sub"><Sub /></TabsContent>
      </Tabs>
    </div>
  );
}

function Of() {
  const [x, setX] = useState(15);
  const [y, setY] = useState(2500);
  const r = useMemo(() => (x / 100) * y, [x, y]);
  return (
    <Card>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Percent">
          <Input type="number" value={x} onChange={(e) => setX(Number(e.target.value) || 0)} />
        </Field>
        <Field label="Of value">
          <Input type="number" value={y} onChange={(e) => setY(Number(e.target.value) || 0)} />
        </Field>
      </div>
      <Result label={`${x}% of ${y.toLocaleString()}`} value={r.toLocaleString(undefined, { maximumFractionDigits: 4 })} />
    </Card>
  );
}

function Is() {
  const [x, setX] = useState(750);
  const [y, setY] = useState(2500);
  const r = useMemo(() => (y === 0 ? 0 : (x / y) * 100), [x, y]);
  return (
    <Card>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Number">
          <Input type="number" value={x} onChange={(e) => setX(Number(e.target.value) || 0)} />
        </Field>
        <Field label="Out of">
          <Input type="number" value={y} onChange={(e) => setY(Number(e.target.value) || 0)} />
        </Field>
      </div>
      <Result label={`${x.toLocaleString()} is what % of ${y.toLocaleString()}`} value={`${r.toFixed(2)} %`} />
    </Card>
  );
}

function Change() {
  const [a, setA] = useState(80);
  const [b, setB] = useState(100);
  const r = useMemo(() => (a === 0 ? 0 : ((b - a) / a) * 100), [a, b]);
  return (
    <Card>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="From">
          <Input type="number" value={a} onChange={(e) => setA(Number(e.target.value) || 0)} />
        </Field>
        <Field label="To">
          <Input type="number" value={b} onChange={(e) => setB(Number(e.target.value) || 0)} />
        </Field>
      </div>
      <Result label="Change" value={`${r > 0 ? '+' : ''}${r.toFixed(2)} %`} />
    </Card>
  );
}

function Add() {
  const [v, setV] = useState(1500);
  const [p, setP] = useState(18);
  const r = useMemo(() => v + (v * p) / 100, [v, p]);
  return (
    <Card>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Value">
          <Input type="number" value={v} onChange={(e) => setV(Number(e.target.value) || 0)} />
        </Field>
        <Field label="Add %">
          <Input type="number" value={p} onChange={(e) => setP(Number(e.target.value) || 0)} />
        </Field>
      </div>
      <Result label={`${v.toLocaleString()} + ${p}%`} value={r.toLocaleString(undefined, { maximumFractionDigits: 4 })} />
    </Card>
  );
}

function Sub() {
  const [v, setV] = useState(2999);
  const [p, setP] = useState(20);
  const r = useMemo(() => v - (v * p) / 100, [v, p]);
  return (
    <Card>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Field label="Value">
          <Input type="number" value={v} onChange={(e) => setV(Number(e.target.value) || 0)} />
        </Field>
        <Field label="Subtract %">
          <Input type="number" value={p} onChange={(e) => setP(Number(e.target.value) || 0)} />
        </Field>
      </div>
      <Result label={`${v.toLocaleString()} − ${p}%`} value={r.toLocaleString(undefined, { maximumFractionDigits: 4 })} />
    </Card>
  );
}

function Result({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-4 rounded-xl bg-bg-inset border border-border">
      <div className="text-xs text-fg-muted">{label}</div>
      <div className="text-3xl font-mono font-semibold mt-2 tabular-nums">{value}</div>
    </div>
  );
}

export default PercentageCalculator;
